import { useId, type ChangeEvent, type ReactNode } from 'react';
import { IconAlert, IconCheck } from './icons.js';

/*
 * Stavebne prvky rozhrania.
 *
 * Vsetky farby, polomery a tiene chodia z dizajnovych tokenov; v tomto
 * subore nie je ziadna natvrdo napisana hodnota. Ked bude treba
 * komponent zmenit, meni sa tu - nie na desiatich obrazovkach.
 */

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'accent';

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-(--color-text-muted)',
  ok: 'text-(--color-ok)',
  warn: 'text-(--color-warn)',
  danger: 'text-(--color-danger)',
  accent: 'text-(--color-accent)',
};

const TONE_BADGE: Record<Tone, string> = {
  neutral: 'bg-(--color-surface-sunken) text-(--color-text-muted)',
  ok: 'bg-(--color-ok-soft) text-(--color-ok)',
  warn: 'bg-(--color-warn-soft) text-(--color-warn)',
  danger: 'bg-(--color-danger-soft) text-(--color-danger)',
  accent: 'bg-(--color-accent-soft) text-(--color-accent)',
};

// --- plochy ---------------------------------------------------------------

export function Card({
  children,
  className = '',
  as: Tag = 'div',
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly as?: 'div' | 'li' | 'section' | 'article';
}): ReactNode {
  return (
    <Tag
      className={`rounded-(--radius-card) border border-(--color-border) bg-(--color-surface-raised) shadow-(--shadow-card) ${className}`}
    >
      {children}
    </Tag>
  );
}

export function PageHeader({
  title,
  detail,
  action,
}: {
  readonly title: string;
  readonly detail?: string | undefined;
  readonly action?: ReactNode | undefined;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      {/*
       * `min-w-0` a zalamovanie: nadpis je pri detaile nazov zariadenia,
       * teda text od pouzivatela. Dlhy nazov bez medzery inak rozsiril
       * celu stranku (hlasene 2026-09-17).
       */}
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight break-words text-(--color-text)">
          {title}
        </h1>
        {detail !== undefined && (
          <p className="max-w-prose text-sm text-(--color-text-muted)">{detail}</p>
        )}
      </div>
      {action}
    </div>
  );
}

export function SectionTitle({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <h2 className="text-xs font-semibold tracking-wider text-(--color-text-subtle) uppercase">
      {children}
    </h2>
  );
}

// --- ovladacie prvky ------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON: Record<ButtonVariant, string> = {
  primary:
    'bg-(--color-accent) text-(--color-accent-text) hover:bg-(--color-accent-hover) border-transparent',
  secondary:
    'bg-(--color-surface-raised) text-(--color-text) border-(--color-border-strong) hover:bg-(--color-surface-sunken)',
  ghost:
    'bg-transparent text-(--color-text-muted) border-transparent hover:bg-(--color-surface-sunken) hover:text-(--color-text)',
  danger:
    'bg-transparent text-(--color-danger) border-(--color-border) hover:bg-(--color-danger-soft)',
};

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'secondary',
  disabled = false,
  icon,
  title,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: 'button' | 'submit';
  readonly variant?: ButtonVariant;
  readonly disabled?: boolean;
  readonly icon?: ReactNode;
  readonly title?: string;
}): ReactNode {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      onClick={onClick}
      disabled={disabled}
      {...(title !== undefined ? { title } : {})}
      className={`inline-flex items-center justify-center gap-2 rounded-(--radius-control) border px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55 ${BUTTON[variant]}`}
    >
      {icon}
      {children}
    </button>
  );
}

/** Ikonove tlacidlo. `label` je povinny - inak by ho citacka neprecitala. */
export function IconButton({
  label,
  icon,
  onClick,
  disabled = false,
  tone = 'neutral',
}: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly tone?: Tone;
}): ReactNode {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex size-9 items-center justify-center rounded-(--radius-control) transition-colors hover:bg-(--color-surface-sunken) disabled:cursor-not-allowed disabled:opacity-50 ${TONE_TEXT[tone]}`}
    >
      {icon}
    </button>
  );
}

/**
 * Prepinac zapnute/vypnute.
 *
 * Je to `role="switch"` na tlacidle, nie stylovany checkbox: citacka tak
 * povie "prepinac, zapnuty" namiesto "zaskrtavacie policko".
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  readonly checked: boolean;
  readonly onChange: (hodnota: boolean) => void;
  readonly label: string;
  readonly disabled?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-(--radius-pill) border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        checked
          ? 'border-transparent bg-(--color-accent)'
          : 'border-(--color-border-strong) bg-(--color-surface-sunken)'
      }`}
    >
      <span
        className={`inline-block size-4.5 rounded-(--radius-pill) bg-(--color-surface-raised) shadow-(--shadow-card) transition-transform ${
          checked ? 'translate-x-5.5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  unit,
  disabled = false,
  onChange,
  onCommit,
}: {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly unit?: string;
  readonly disabled?: boolean;
  readonly onChange: (hodnota: number) => void;
  readonly onCommit: (hodnota: number) => void;
}): ReactNode {
  const id = useId();
  const zmena = (e: ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value));
  const potvrd = (e: { readonly currentTarget: HTMLInputElement }) =>
    onCommit(Number(e.currentTarget.value));

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="text-sm text-(--color-text-muted)">
          {label}
        </label>
        <span className="text-sm font-medium text-(--color-text)">
          {value}
          {unit ?? ''}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={zmena}
        // Prikaz sa posiela az pri pusteni: inak by tahanie posuvnika
        // poslalo do zariadenia stovky prikazov.
        onMouseUp={potvrd}
        onTouchEnd={potvrd}
        onKeyUp={potvrd}
      />
    </div>
  );
}

export function Field({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required = false,
  autoComplete,
  placeholder,
  akcia,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (hodnota: string) => void;
  readonly type?: 'text' | 'password' | 'number';
  readonly hint?: string;
  readonly required?: boolean;
  readonly autoComplete?: string;
  readonly placeholder?: string;
  /**
   * Tlačidlo v pravej časti poľa — napríklad načítanie hodnoty kamerou.
   *
   * Je mimo rámika vstupu, nie v ňom: vstup si tak drží vlastný focus
   * a klávesnicou sa dá prejsť na tlačidlo ako na samostatný prvok.
   */
  readonly akcia?: ReactNode;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-(--color-text)">
        {label}
      </label>
      <div className="flex items-stretch gap-2">
        <input
          id={id}
          type={type}
          value={value}
          required={required}
          {...(autoComplete !== undefined ? { autoComplete } : {})}
          {...(placeholder !== undefined ? { placeholder } : {})}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-w-0 flex-1 rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface) px-3 py-2 text-sm text-(--color-text) placeholder:text-(--color-text-subtle)"
        />
        {akcia !== undefined && <div className="shrink-0">{akcia}</div>}
      </div>
      {hint !== undefined && <p className="text-xs text-(--color-text-subtle)">{hint}</p>}
    </div>
  );
}

// --- oznamenia ------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
}: {
  readonly children: ReactNode;
  readonly tone?: Tone;
}): ReactNode {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-(--radius-pill) px-2.5 py-1 text-xs font-medium ${TONE_BADGE[tone]}`}
    >
      {children}
    </span>
  );
}

/** Farebna bodka stavu. Nesie vyznam len spolu s textom vedla nej. */
export function StatusDot({ tone }: { readonly tone: Tone }): ReactNode {
  const farba =
    tone === 'ok'
      ? 'bg-(--color-ok)'
      : tone === 'warn'
        ? 'bg-(--color-warn)'
        : tone === 'danger'
          ? 'bg-(--color-danger)'
          : 'bg-(--color-text-subtle)';
  return (
    // `shrink-0`: bodka je znak stavu, nesmie sa v uzkom riadku splostit.
    <span
      aria-hidden="true"
      className={`inline-block size-2 shrink-0 rounded-(--radius-pill) ${farba}`}
    />
  );
}

/**
 * Oznamenie k akcii.
 *
 * `alert` pre chybu, `status` pre uspech - citacka tak chybu precita
 * hned, uspech az ked na to pride rad.
 */
export function Notice({
  tone,
  children,
}: {
  readonly tone: 'ok' | 'danger' | 'warn';
  readonly children: ReactNode;
}): ReactNode {
  const styl =
    tone === 'ok'
      ? 'border-(--color-ok) bg-(--color-ok-soft) text-(--color-ok)'
      : tone === 'warn'
        ? 'border-(--color-warn) bg-(--color-warn-soft) text-(--color-warn)'
        : 'border-(--color-danger) bg-(--color-danger-soft) text-(--color-danger)';
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      aria-live={tone === 'danger' ? 'assertive' : 'polite'}
      className={`flex items-start gap-2.5 rounded-(--radius-control) border px-3.5 py-2.5 text-sm ${styl}`}
    >
      <span className="mt-0.5 shrink-0">
        {tone === 'ok' ? <IconCheck className="size-4" /> : <IconAlert className="size-4" />}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Dvojica popis - hodnota, pouzita v detailoch. */
export function DataRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-(--color-border) py-2.5 last:border-b-0">
      <dt className="text-sm text-(--color-text-muted)">{label}</dt>
      {/* Hodnotou byva aj dlhy identifikator; musi sa zalomit, nie pretiect. */}
      <dd className="min-w-0 text-sm break-words text-(--color-text)">{children}</dd>
    </div>
  );
}
